import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import * as Icons from 'lucide-react';
import { useOrganizationAppStore } from '@/hooks/useOrganizationAppStore';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { isAppStoreApp } from '@/lib/apps';
import type { App } from '@/services/AppService';
import { useToast } from '@/hooks/use-toast';

export interface AppDefinition {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  icon_name: string; // Add original icon name from database
  component: ReactNode;
  component_type: string; // Add component type from database
  category: 'productivity' | 'communication' | 'utilities' | 'system';
  isInstalled: boolean;
  isInDock: boolean;
}

export interface DockItem {
  id: string;
  type: 'static' | 'app';
  name: string;
  icon: React.ComponentType<any>;
  component?: ReactNode;
  isInstalled?: boolean;
}

interface AppsManagerContextType {
  apps: AppDefinition[];
  installedApps: AppDefinition[];
  dockApps: AppDefinition[];
  unifiedDock: DockItem[];
  runningApps: string[];
  installApp: (appId: string) => void;
  uninstallApp: (appId: string) => void;
  addToDock: (appId: string) => Promise<void>;
  removeFromDock: (appId: string) => Promise<void>;
  removeFromUnifiedDock: (itemId: string) => void;
  openApp: (appId: string) => void;
  closeApp: (appId: string) => void;
  reorderDockApps: (sourceIndex: number, destinationIndex: number) => void;
  reorderUnifiedDock: (sourceIndex: number, destinationIndex: number) => void;
}

const AppsManagerContext = createContext<AppsManagerContextType | undefined>(undefined);

// Helper function to get the correct icon component
const getIconComponent = (iconName: string): React.ComponentType<any> => {
  // @ts-ignore - Dynamic icon access
  return Icons[iconName] || Icons.Store; // Fallback to Store if icon not found
};

// Helper function to get the actual component for an app
const getAppComponent = (componentType: string): ReactNode => {
  switch (componentType) {
    case 'AppStoreWindow':
      const AppStoreWindow = React.lazy(() => import('@/components/windows/AppStoreWindow'));
      return <AppStoreWindow />;
    case 'RoleManagementWindow':
      const RoleManagementWindow = React.lazy(
        () => import('@/components/windows/RoleManagementWindow'),
      );
      return <RoleManagementWindow />;
    case 'UserRoleManagementWindow':
      const UserRoleManagementWindow = React.lazy(
        () => import('@/components/windows/UserRoleManagementWindow'),
      );
      return <UserRoleManagementWindow />;
    case 'PermissionAuditLogWindow':
      const PermissionAuditLogWindow = React.lazy(
        () => import('@/components/windows/PermissionAuditLogWindow'),
      );
      return <PermissionAuditLogWindow />;
    case 'OrganizationSettingsWindow':
      const OrganizationSettingsWindow = React.lazy(
        () => import('@/components/windows/OrganizationSettingsWindow'),
      );
      return <OrganizationSettingsWindow />;
    case 'EuclidWindow':
      const EuclidWindow = React.lazy(() => import('@/components/windows/EuclidWindow'));
      return <EuclidWindow />;

    case 'CollectionCustomizerWindow':
      const CollectionCustomizerWindow = React.lazy(
        () => import('@/components/windows/CollectionCustomizerWindow'),
      );
      return <CollectionCustomizerWindow />;
    case 'LifeInsuranceWindow':
      const LifeInsuranceWindow = React.lazy(
        () => import('@/components/windows/LifeInsuranceWindow'),
      );
      return <LifeInsuranceWindow />;
    case 'CopilotWindow':
      const CopilotWindow = React.lazy(() => import('@/components/windows/CopilotWindow'));
      return <CopilotWindow />;
    case 'VersionChangelogWindow':
      const VersionChangelogWindow = React.lazy(
        () => import('@/components/windows/VersionChangelogWindow'),
      );
      return <VersionChangelogWindow />;
    case 'NotificationSettingsWindow':
      const NotificationSettingsWindow = React.lazy(
        () => import('@/components/windows/NotificationSettingsWindow'),
      );
      return <NotificationSettingsWindow />;
    case 'FocusModeEditorWindow':
      const FocusModeEditorWindow = React.lazy(
        () => import('@/components/windows/FocusModeEditorWindow'),
      );
      return <FocusModeEditorWindow />;
    case 'FileManagerWindow':
      const FileManagerWindow = React.lazy(() => import('@/components/windows/FileManagerWindow'));
      return <FileManagerWindow />;
    case 'TeamChatWindow':
      const TeamChatWindow = React.lazy(() => import('@/components/windows/TeamChatWindow'));
      return <TeamChatWindow />;
    case 'SoundStageWindow':
      const SoundStageWindow = React.lazy(() => import('@/components/windows/SoundStageWindow'));
      return <SoundStageWindow />;
    case 'OpenQuoteWindow':
      const OpenQuoteWindow = React.lazy(() => import('@/components/windows/OpenQuoteWindow'));
      return <OpenQuoteWindow />;
    case 'TomorrowLifeWindow':
      const TomorrowLifeWindow = React.lazy(() => import('@/components/windows/TomorrowLifeWindow'));
      return <TomorrowLifeWindow />;
    case 'HealthSherpaWindow':
      const HealthSherpaWindow = React.lazy(() => import('@/components/windows/HealthSherpaWindow'));
      return <HealthSherpaWindow />;
    case 'HealthSherpaMedicareWindow':
      const HealthSherpaMedicareWindow = React.lazy(() => import('@/components/windows/HealthSherpaMedicareWindow'));
      return <HealthSherpaMedicareWindow />;
    default:
      return null;
  }
};

// Helper function to convert Supabase App to AppDefinition
const convertToAppDefinition = (
  app: any,
  isInstalled: boolean,
  isInDock: boolean,
): AppDefinition => ({
  id: app.id,
  name: app.name,
  description: app.description || '',
  icon: getIconComponent(app.icon_name),
  icon_name: app.icon_name, // Keep original icon name
  component: getAppComponent(app.component_type),
  component_type: app.component_type, // Keep original component type
  category: app.category as 'productivity' | 'communication' | 'utilities' | 'system',
  isInstalled,
  isInDock,
});

export function AppsManagerProvider({ children }: { children: ReactNode }) {
  const orgContext = useOrganizationContext();
  const [localLoading, setLocalLoading] = useState(true);

  // Don't render children until organization context is ready
  useEffect(() => {
    if (orgContext && !orgContext.loading) {
      setLocalLoading(false);
    }
  }, [orgContext?.loading]);

  // If organization context is not available yet or still loading, provide safe defaults
  if (!orgContext || orgContext.loading || localLoading) {
    return (
      <AppsManagerContext.Provider
        value={{
          apps: [],
          installedApps: [],
          dockApps: [],
          unifiedDock: [
            {
              id: 'home',
              type: 'static' as const,
              name: 'Clean Workbench',
              icon: () => null,
            },
            {
              id: 'notes',
              type: 'static' as const,
              name: 'Notes',
              icon: () => null,
            },
          ],
          runningApps: [],
          installApp: async () => {},
          uninstallApp: async () => {},
          addToDock: async () => {},
          removeFromDock: async () => {},
          removeFromUnifiedDock: () => {},
          openApp: () => {},
          closeApp: () => {},
          reorderDockApps: () => {},
          reorderUnifiedDock: () => {},
        }}
      >
        {children}
      </AppsManagerContext.Provider>
    );
  }

  return <AppsManagerProviderInner>{children}</AppsManagerProviderInner>;
}

function AppsManagerProviderInner({ children }: { children: ReactNode }) {
  const {
    apps,
    organizationInstalledApps,
    currentOrganizationId,
    loading,
    isAppInstalled,
    isAppInDock,
    installApp: installAppAction,
    uninstallApp: uninstallAppAction,
    addToDock: addToDockAction,
    removeFromDock: removeFromDockAction,
    updateLastUsed,
  } = useOrganizationAppStore();
  const { toast } = useToast();
  const [runningApps, setRunningApps] = useState<string[]>([]);

  // Organization-specific dock order
  const getDockOrderKey = () => {
    return currentOrganizationId ? `org_${currentOrganizationId}_unified-dock-order` : null;
  };

  const [unifiedDockOrder, setUnifiedDockOrder] = useState<string[]>(() => {
    const key = getDockOrderKey();
    const saved = key ? localStorage.getItem(key) : null;
    // Default order includes system apps first
    const defaultOrder = ['home', 'notes'];
    return saved ? JSON.parse(saved) : defaultOrder;
  });

  // Force re-compute when organizationInstalledApps changes
  const [, forceUpdate] = useState({});

  // Reload dock order when organization changes
  useEffect(() => {
    const key = getDockOrderKey();
    if (!key) {
      setUnifiedDockOrder(['home', 'notes']);
      return;
    }

    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setUnifiedDockOrder(parsed);
      } catch (e) {
        console.error('Failed to parse dock order:', e);
        setUnifiedDockOrder(['home', 'notes']);
      }
    } else {
      setUnifiedDockOrder(['home', 'notes']);
    }
  }, [currentOrganizationId]);

  // Convert database apps to AppDefinition format
  // This recalculates whenever organizationInstalledApps changes
  const allApps = useMemo(() => {
    return apps.map((app) => {
      const isInstalled = isAppInstalled(app.id);
      const isInDock = isAppInDock(app.id);
      return convertToAppDefinition(app, isInstalled, isInDock);
    });
  }, [apps, organizationInstalledApps]);

  const installedApps = useMemo(() => {
    const filtered = allApps.filter((app) => app.isInstalled);
    return filtered;
  }, [allApps]);

  const dockApps = useMemo(() => {
    const filtered = allApps.filter((app) => app.isInDock);
    return filtered;
  }, [allApps]);

  // Create unified dock with static icons and apps - memoized for proper reactivity
  const unifiedDock: DockItem[] = useMemo(() => {
    const dock = unifiedDockOrder
      .map((id) => {
        if (id === 'home') {
          return {
            id: 'home',
            type: 'static' as const,
            name: 'Clean Workbench',
            icon: () => null, // Will be handled in dock component
          };
        }
        if (id === 'notes') {
          return {
            id: 'notes',
            type: 'static' as const,
            name: 'Notes',
            icon: () => null, // Will be handled in dock component
          };
        }
        // Find app by id (UUID from database)
        const app = dockApps.find((app) => app.id === id);
        if (app) {
          return {
            id: app.id,
            type: 'app' as const,
            name: app.name,
            icon: app.icon,
            component: app.component,
            isInstalled: app.isInstalled,
          };
        }
        return null;
      })
      .filter(Boolean) as DockItem[];

    // Dock now respects database state - only shows apps where is_in_dock = true
    return dock;
  }, [unifiedDockOrder, dockApps]);

  const installApp = (appId: string) => {
    const app = apps.find((a) => a.id === appId);
    if (app) {
      installAppAction(appId, app.name);
    }
  };

  const uninstallApp = (appId: string) => {
    const app = apps.find((a) => a.id === appId);
    if (app) {
      // Check if it's a system app (cannot uninstall)
      if (app.metadata && (app.metadata as any).system) {
        return; // Silently ignore system app uninstall attempts
      }
      uninstallAppAction(appId, app.name);
    }
  };

  const addToDock = async (appId: string) => {
    console.log('🎯 AppsManager.addToDock called with appId:', appId);
    const app = apps.find((a) => a.id === appId);

    if (!app) {
      console.error('❌ App not found with ID:', appId);
      return;
    }

    console.log('📦 Found app:', app.name, 'Component:', app.component_type);

    // Check if app is installed first
    const isInstalled = isAppInstalled(appId);
    console.log('📱 Is app installed?', isInstalled);

    if (!isInstalled) {
      console.log('⚠️ App not installed, installing first...');
      await installAppAction(appId, app.name);
      // Wait a moment for the installation to process
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    console.log('🚀 Calling addToDockAction...');
    const success = await addToDockAction(appId, app.name);
    console.log('✅ AddToDock success:', success);

    if (success) {
      // Add the app to the unified dock order if it's not already there
      console.log('📝 Updating unified dock order...');
      setUnifiedDockOrder((prev) => {
        console.log('   Current order:', prev);
        if (!prev.includes(appId)) {
          const newOrder = [...prev, appId];
          console.log('   New order:', newOrder);
          const key = getDockOrderKey();
          if (key) localStorage.setItem(key, JSON.stringify(newOrder));
          return newOrder;
        }
        console.log('   App already in order');
        return prev;
      });
    }
  };

  const removeFromDock = async (appId: string) => {
    console.log('🎯 AppsManager.removeFromDock called with:', appId);
    const app = apps.find((a) => a.id === appId);

    if (!app) {
      console.error('❌ AppsManager: App not found:', appId);
      toast({
        title: 'Error',
        description: 'App not found',
        variant: 'destructive',
      });
      return;
    }

    console.log('✅ AppsManager: App found:', app.name, app.id);

    // Check if it's the App Store (cannot remove from dock)
    if (isAppStoreApp(app)) {
      console.log('⛔ AppsManager: Cannot remove App Store');
      toast({
        title: 'Cannot Remove App Store',
        description: 'The App Store cannot be removed from the dock.',
        variant: 'destructive',
      });
      return;
    }

    console.log('📞 AppsManager: Calling removeFromDockAction...');
    const success = await removeFromDockAction(appId, app.name);
    console.log('📬 AppsManager: removeFromDockAction result:', success);

    // Note: removeFromDockAction already shows toast, so we don't show another one
  };

  const openApp = (appId: string) => {
    console.log('🎮 Opening app:', appId);

    // Special logging for Organization Settings
    if (appId === 'home' || appId === 'notes') {
      console.log('   Static app detected');
    } else {
      const app = apps.find((a) => a.id === appId);
      if (app) {
        console.log('   App details:', {
          name: app.name,
          component_type: app.component_type,
          is_published: app.is_published,
          installed: isAppInstalled(appId),
          in_dock: isAppInDock(appId),
        });

        // Extra logging for Organization Settings
        if (app.component_type === 'OrganizationSettingsWindow') {
          console.log('🏁 ORGANIZATION SETTINGS OPENED!');
          console.log('   All installed apps:', organizationInstalledApps);
          console.log(
            '   Is installed for this org?',
            organizationInstalledApps.some((ia) => ia.app_id === appId),
          );
        }
      } else {
        console.error('   App not found in apps list!');
      }
    }

    setRunningApps((prev) => [...new Set([...prev, appId])]);
    // Update last used timestamp
    updateLastUsed(appId);
  };

  const closeApp = (appId: string) => {
    setRunningApps((prev) => prev.filter((id) => id !== appId));
  };

  const reorderDockApps = (sourceIndex: number, destinationIndex: number) => {
    // This functionality will need to be implemented with Supabase
    // For now, we'll keep the local implementation for static reordering
    console.log('Dock reordering not yet implemented with Supabase backend');
  };

  const reorderUnifiedDock = (sourceIndex: number, destinationIndex: number) => {
    // Don't allow moving Home icon (always at index 0)
    if (sourceIndex === 0 || destinationIndex === 0) return;

    const newOrder = Array.from(unifiedDockOrder);
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(destinationIndex, 0, removed);

    setUnifiedDockOrder(newOrder);
    const key = getDockOrderKey();
    if (key) localStorage.setItem(key, JSON.stringify(newOrder));
  };

  const removeFromUnifiedDock = (itemId: string) => {
    console.log('📍 Removing from unified dock order:', itemId);
    const newOrder = unifiedDockOrder.filter((id) => id !== itemId);
    setUnifiedDockOrder(newOrder);
    const key = getDockOrderKey();
    if (key) localStorage.setItem(key, JSON.stringify(newOrder));
  };

  // Update unified dock when dock apps change
  useEffect(() => {
    const currentAppIds = dockApps.map((app) => app.id);

    // Add any new dock apps to the unified dock order
    const newAppIds = currentAppIds.filter((id) => !unifiedDockOrder.includes(id));
    if (newAppIds.length > 0) {
      setUnifiedDockOrder((prev) => {
        const newOrder = [...prev, ...newAppIds];
        const key = getDockOrderKey();
        if (key) localStorage.setItem(key, JSON.stringify(newOrder));
        return newOrder;
      });
    }
    const staticIds = ['home', 'notes'];

    // Get system apps first (using component_type instead of metadata)
    const systemApps = installedApps.filter((app) => {
      const appFromStore = apps.find((a) => a.id === app.id);
      return appFromStore?.component_type === 'AppStoreWindow';
    });

    // Clean up old localStorage entries that use hardcoded IDs like 'app-store'
    const cleanedOrder = unifiedDockOrder.filter((id) => {
      // Keep static IDs
      if (staticIds.includes(id)) return true;
      // Keep valid UUID format (system or regular apps)
      return currentAppIds.includes(id) || systemApps.some((app) => app.id === id);
    });

    // Add new dock apps to unified order if not already present
    const newOrder = [...cleanedOrder];
    const systemAppIds: string[] = [];
    const regularAppIds: string[] = [];

    currentAppIds.forEach((appId) => {
      if (!newOrder.includes(appId)) {
        const app = apps.find((a) => a.id === appId);
        if (app?.component_type === 'AppStoreWindow') {
          systemAppIds.push(appId);
        } else {
          regularAppIds.push(appId);
        }
      }
    });

    // Insert system apps after static items, regular apps at the end
    let insertIndex = newOrder.findIndex((id) => !staticIds.includes(id));
    if (insertIndex === -1) insertIndex = newOrder.length;

    systemAppIds.reverse().forEach((appId) => {
      newOrder.splice(insertIndex, 0, appId);
    });
    regularAppIds.forEach((appId) => {
      newOrder.push(appId);
    });

    // Remove apps that are no longer in dock
    const filteredOrder = newOrder.filter(
      (id) => staticIds.includes(id) || currentAppIds.includes(id),
    );

    if (JSON.stringify(filteredOrder) !== JSON.stringify(unifiedDockOrder)) {
      setUnifiedDockOrder(filteredOrder);
      const key = getDockOrderKey();
      if (key) localStorage.setItem(key, JSON.stringify(filteredOrder));
    }
  }, [dockApps, unifiedDockOrder, apps, installedApps]);

  return (
    <AppsManagerContext.Provider
      value={{
        apps: allApps,
        installedApps,
        dockApps,
        unifiedDock,
        runningApps,
        installApp,
        uninstallApp,
        addToDock,
        removeFromDock,
        removeFromUnifiedDock,
        openApp,
        closeApp,
        reorderDockApps,
        reorderUnifiedDock,
      }}
    >
      {children}
    </AppsManagerContext.Provider>
  );
}

export function useAppsManager() {
  const context = useContext(AppsManagerContext);
  if (context === undefined) {
    throw new Error('useAppsManager must be used within an AppsManagerProvider');
  }
  return context;
}
